import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { DistanceSlider, Text } from "@src/appV2/redesign/system/ui-components";
import { useCallback, useEffect, useRef, useState } from "react";

import type { UserFilters } from "../types";

interface DistanceFilterProps {
  value: UserFilters["distance"];
  onChange: (value: UserFilters["distance"]) => void;
}

export function DistanceFilter(props: DistanceFilterProps) {
  const { value, onChange } = props;

  const [localValue, setLocalValue] = useState(value);
  const pendingDistanceRef = useRef<number | undefined>(undefined);

  const handleDebouncedSave = useCallback(
    (newValue: number) => {
      onChange(newValue);
    },
    [onChange]
  );

  useDebounce({
    value: localValue,
    debounceTimeInMs: 300,
    onUpdateValue: handleDebouncedSave,
  });

  useEffect(() => {
    if (!isDefined(pendingDistanceRef.current) || pendingDistanceRef.current === value) {
      pendingDistanceRef.current = undefined;
      setLocalValue(value);
    }
  }, [value]);

  return (
    <Stack spacing={5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text semibold variant="body1">
          Distance From Home
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.border?.subtle}`,
            borderRadius: theme.borderRadius?.xSmall,
            padding: 3,
          })}
        >
          {localValue} miles
        </Text>
      </Stack>

      <Stack>
        <DistanceSlider
          aria-label="Shift Filters Distance Slider"
          value={localValue}
          options={[10, 50, 100, 150]}
          step={10}
          max={150}
          min={10}
          onChange={(_event, sliderValue) => {
            const newValue = sliderValue as number;
            pendingDistanceRef.current = newValue;
            setLocalValue(newValue);
          }}
        />
      </Stack>
    </Stack>
  );
}
